import AirDatepicker from 'air-datepicker'
import 'air-datepicker/air-datepicker.css'
import localeEn from 'air-datepicker/locale/en'
import localeUk from 'air-datepicker/locale/uk'
import { Dropdown, theme } from 'antd'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CiCalendarDate } from 'react-icons/ci'
import { configManager } from '../../../Managers/Config'
import { AntdConfig } from '@/Ship/Configs/AntdConfig'
import type { FieldMethods } from '../types'
import { bindCloseDatepickerOnScroll } from './closeDatepickerOnScroll'
import { flipDatepickerIfClipped } from './datepickerPosition'
import './DatePickerInput.css'

dayjs.extend(utc)
dayjs.extend(timezone)

// Статичне обчислення токенів з AntdConfig (без React-контексту) — аналогічно DatePickerInput.tsx
const _st = (() => { try { return (theme as any).getDesignToken?.(AntdConfig.theme) } catch { return null } })()
const _cfg = AntdConfig.theme?.token as Record<string, string> | undefined
const adpTheme = {
    primary:        _st?.colorPrimary           ?? _cfg?.colorPrimary         ?? '#e84923',
    primaryHover:   _st?.colorPrimaryHover      ??                               '#ff6b45',
    primaryBgHover: _st?.colorPrimaryBgHover    ??                               'rgba(232,73,35,0.2)',
    primaryBg:      _st?.colorPrimaryBg         ??                               'rgba(232,73,35,0.1)',
    bg:             _st?.colorBgElevated        ??                               '#1f1f1f',
    bgHover:        _st?.colorFillSecondary     ??                               'rgba(255,255,255,0.08)',
    bgActive:       _st?.colorFill              ??                               'rgba(255,255,255,0.12)',
    text:           _st?.colorText              ??                               'rgba(255,255,255,0.85)',
    textSec:        _st?.colorTextSecondary     ??                               'rgba(255,255,255,0.45)',
    textOther:      (_st?.colorTextQuaternary ?? _st?.colorTextTertiary)     ??  'rgba(255,255,255,0.25)',
    textDisabled:   _st?.colorTextDisabled      ??                               'rgba(255,255,255,0.25)',
    border:         _st?.colorBorder            ?? _cfg?.colorBorder         ?? 'rgba(255,255,255,0.15)',
    borderInner:    _st?.colorBorderSecondary   ?? _cfg?.colorBorderSecondary ?? 'rgba(255,255,255,0.08)',
}

export interface DatePickerRangeInputProps {
    field?: FieldMethods
    index?: number
    disabled?: boolean
    className?: string
    style?: React.CSSProperties
    options?: Record<string, any>
    [key: string]: any
}

export const DatePickerRangeInput: React.FC<DatePickerRangeInputProps> = observer(
    ({ field, index, options = {} }) => {
        const { t } = useTranslation()
        const fieldHiddenRef = useRef<HTMLInputElement>(null)
        const datepickerRef = useRef<AirDatepicker | null>(null)
        const AppConfig = configManager.get('AppConfig')

        // Отримуємо формат дати з конфігурації
        const dateFormat = AppConfig?.dateDefaultFormat || 'YYYY-MM-DD'

        // Інжектуємо <style> тег із точними token-значеннями для .air-datepicker {}
        // (аналогічно DatePickerInput.tsx — div STYLE_ID спільний між ними)
        useEffect(() => {
            const STYLE_ID = 'wl-adp-theme'
            let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null
            if (!el) {
                el = document.createElement('style')
                el.id = STYLE_ID
                document.head.appendChild(el)
            }
            el.textContent = [
                '.air-datepicker {',
                `  --adp-primary-color: ${adpTheme.primary};`,
                `  --adp-primary-hover: ${adpTheme.primaryHover};`,
                `  --adp-primary-bg-hover: ${adpTheme.primaryBgHover};`,
                `  --adp-primary-bg: ${adpTheme.primaryBg};`,
                `  --adp-background-color: ${adpTheme.bg};`,
                `  --adp-background-color-hover: ${adpTheme.bgHover};`,
                `  --adp-background-color-active: ${adpTheme.bgActive};`,
                `  --adp-color: ${adpTheme.text};`,
                `  --adp-color-secondary: ${adpTheme.textSec};`,
                `  --adp-color-other-month: ${adpTheme.textOther};`,
                `  --adp-color-disabled: ${adpTheme.textDisabled};`,
                `  --adp-border-color: ${adpTheme.border};`,
                `  --adp-border-color-inner: ${adpTheme.borderInner};`,
                `  --adp-border-color-inline: ${adpTheme.borderInner};`,
                '}',
            ].join('\n')
        }, [])

        if (!field) {
            return null
        }

        const optionsRange = {
            range: true,
            timepicker: false,
            ...options,
            multipleDatesSeparator: ' - ',
        }

        const { onChange, ...props } = field.bind({ index: index ?? 0 })

        const IconObserver = observer(() => {
            return (
                <>
                    {field.value && !props?.disabled && (
                        <span
                            className="wl-datepicker-input-clear ant-select-clear"
                            unselectable="on"
                            aria-hidden="true"
                            style={{ userSelect: 'none' }}
                            onClick={() => {
                                onChange(null)
                            }}
                        >
                            <span
                                role="img"
                                aria-label="close-circle"
                                className="anticon anticon-close-circle"
                            >
                                <svg
                                    fillRule="evenodd"
                                    viewBox="64 64 896 896"
                                    focusable="false"
                                    data-icon="close-circle"
                                    width="1em"
                                    height="1em"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M512 64c247.4 0 448 200.6 448 448S759.4 960 512 960 64 759.4 64 512 264.6 64 512 64zm127.98 274.82h-.04l-.08.06L512 466.75 384.14 338.88c-.04-.05-.06-.06-.08-.06a.12.12 0 00-.07 0c-.03 0-.05.01-.09.05l-45.02 45.02a.2.2 0 00-.05.09.12.12 0 000 .07v.02a.27.27 0 00.06.06L466.75 512 338.88 639.86c-.05.04-.06.06-.06.08a.12.12 0 000 .07c0 .03.01.05.05.09l45.02 45.02a.2.2 0 00.09.05.12.12 0 00.07 0c.02 0 .04-.01.08-.05L512 557.25l127.86 127.87c.04.04.06.05.08.05a.12.12 0 00.07 0c.03 0 .05-.01.09-.05l45.02-45.02a.2.2 0 00.05-.09.12.12 0 000-.07v-.02a.27.27 0 00-.05-.06L557.25 512l127.87-127.86c.04-.04.05-.06.05-.08a.12.12 0 000-.07c0-.03-.01-.05-.05-.09l-45.02-45.02a.2.2 0 00-.09-.05.12.12 0 00-.07 0z"></path>
                                </svg>
                            </span>
                        </span>
                    )}
                    {field.value && !props?.disabled && (
                        <span className="wl-datepicker-suffix">
                            <CiCalendarDate />
                        </span>
                    )}
                    {!field.value && !props?.disabled && (
                        <span className="wl-datepicker-suffix no-hide">
                            <CiCalendarDate />
                        </span>
                    )}
                </>
            )
        })

        let datepickerOptions: any = {
            // isMobile навмисно не вмикаємо — див. коментар у DatePickerInput.tsx.
            // Мобільний режим створює повноекранний затемнюючий оверлей, який іноді
            // не закривається (втрачений transitionend при ре-рендері / пересозданні
            // пікера) і блокує сторінку. Без нього — звичайний popup біля поля.
            timepicker: false,
            locale: AppConfig?.currentLocale === 'uk_UA' ? localeUk : localeEn,
            dateFormat: 'yyyy-MM-dd',
            // Календар не має відкриватися за межу екрана — те саме, що в
            // DatePickerInput. Опції спільні для всіх трьох пікерів (основний і ті,
            // що пересоздають пункти «Вибрати дату» / «Вибрати період»), тож
            // беремо поточний екземпляр із ref, а не з замикання.
            onShow: (isAnimationComplete: boolean) => {
                if (!isAnimationComplete) {
                    flipDatepickerIfClipped(datepickerRef.current)
                }
            },
        }

        let toPushButtons: any[] = []

        if (Array.isArray(field.props?.buttons)) {
            field.props.buttons.map(({ onClick, ...button }: any) => {
                if (typeof onClick === 'function') {
                    button.onClick = () => onClick({ onChange })
                }
                toPushButtons.push(button)
            })
        }

        let items = [
            ...toPushButtons,
            {
                label: t('Select date', { ns: 'Core' }),
                key: 'select-date',
                onClick: () => {
                    if (datepickerRef.current) {
                        datepickerRef.current.destroy()
                    }

                    datepickerRef.current = new AirDatepicker(fieldHiddenRef.current!, {
                        ...datepickerOptions,
                        range: false,
                        onSelect: ({ date }: { date: Date | Date[] }) => {
                            if (date !== undefined && !Array.isArray(date)) {
                                onChange([dayjs(date).startOf('day'), dayjs(date).endOf('day')])
                                datepickerRef.current?.hide()
                            }
                        },
                    })

                    datepickerRef.current.show()
                },
            },
            {
                label: t('Select period', { ns: 'Core' }),
                key: 'select-range',
                onClick: () => {
                    if (datepickerRef.current) {
                        datepickerRef.current.destroy()
                    }

                    datepickerRef.current = new AirDatepicker(fieldHiddenRef.current!, {
                        ...datepickerOptions,
                        range: true,
                        onSelect: ({ date }: { date: Date | Date[] }) => {
                            if (Array.isArray(date) && date.length === 2) {
                                onChange([
                                    dayjs(date[0]).startOf('day'),
                                    dayjs(date[1]).endOf('day'),
                                ])
                                datepickerRef.current?.hide()
                            }
                        },
                    })

                    datepickerRef.current.show()
                },
            },
            {
                label: t('Yesterday', { ns: 'Core' }),
                key: 'yesterday',
                onClick: () => {
                    onChange([
                        dayjs().startOf('day').add(-1, 'day').startOf('day'),
                        dayjs().endOf('day').add(-1, 'day').endOf('day'),
                    ])
                },
            },
            {
                label: t('Today', { ns: 'Core' }),
                key: 'today',
                onClick: () => {
                    onChange([
                        dayjs().startOf('day').startOf('day'),
                        dayjs().endOf('day').endOf('day'),
                    ])
                },
            },
            {
                label: t('Current week', { ns: 'Core' }),
                key: 'current-week',
                onClick: () => {
                    const startOfWeek = dayjs().startOf('week').add(1, 'day').startOf('day')
                    const endOfWeek = dayjs().endOf('week').add(1, 'day').endOf('day')
                    onChange([startOfWeek, endOfWeek])
                },
            },
            {
                label: t('Current month', { ns: 'Core' }),
                key: 'current-month',
                onClick: () => {
                    const startOfMonth = dayjs().startOf('month').startOf('day')
                    const endOfMonth = dayjs().endOf('month').endOf('day')
                    onChange([startOfMonth, endOfMonth])
                },
            },
        ]

        const InputField = observer(() => {
            let value = field.value

            if (Array.isArray(value)) {
                if (typeof value[0] === 'string') {
                    value[0] = dayjs(value[0])
                }
                if (typeof value[1] === 'string') {
                    value[1] = dayjs(value[1])
                }
            }

            const valueFormated = Array.isArray(value)
                ? `${value[0].format(dateFormat)} - ${value[1].format(dateFormat)}`
                : ''

            return (
                <input
                    className="wl-datepicker-input"
                    readOnly={true}
                    {...props}
                    value={valueFormated}
                />
            )
        })

        useEffect(() => {
            datepickerRef.current = new AirDatepicker(fieldHiddenRef.current!, datepickerOptions)

            // Close the calendar on ancestor scroll so it can't detach from the field.
            // The getter always resolves the current picker, including the ones the
            // menu items recreate for the date/period modes.
            const unbindScroll = bindCloseDatepickerOnScroll(() => datepickerRef.current)

            return () => {
                unbindScroll()
                if (datepickerRef.current && typeof datepickerRef.current.destroy === 'function') {
                    datepickerRef.current.destroy()
                    datepickerRef.current = null
                }
            }
        }, [])

        return (
            <div className="wl-datepicker">
                {/* Anchor for AirDatepicker positioning. Must have real geometry
                    (not type="hidden") so the popup positions under the field once
                    isMobile is off; kept visually hidden and non-interactive. */}
                <input
                    ref={fieldHiddenRef}
                    readOnly={true}
                    tabIndex={-1}
                    aria-hidden="true"
                    className="wl-datepicker-range-anchor"
                />
                <Dropdown menu={{ items }} trigger={['click']}>
                    <div>
                        <InputField />
                    </div>
                </Dropdown>
                <IconObserver />
            </div>
        )
    }
)
